<template>
  <div class="amc" ref="root">
    <div
      v-for="card in cards"
      :key="card.key"
      class="amc-card"
      :class="cardClass(card)"
      :draggable="canDrag"
      @dragstart="onDragStart(card, $event)"
      @dragover="onDragOver(card, $event)"
      @dragleave="onDragLeave(card)"
      @drop="onDrop(card, $event)"
      @dragend="onDragEnd"
    >
      <div class="amc-card__head">
        <i class="amc-card__dot" :style="{ background: dotColor(card) }"></i>
        <span class="amc-card__name" :title="card.name">{{ card.name }}</span>
        <!-- 整张卡都能拖，所以关闭图标要单独关掉 draggable，
             不然从它上面按下去会变成拖卡片，点不到删除 -->
        <i
          class="el-icon-close amc-card__remove"
          draggable="false"
          @click="$emit('remove', card.key)"
        ></i>
      </div>
      <div class="amc-card__value">{{ card.text }}</div>
      <!-- 根据对比期开不开来判断这行要不要展示 -->
      <div v-if="showRatio" class="amc-card__ratio">
        <span class="amc-card__ratio-label">{{ t('vsCompare') }}</span>
        <span v-if="hasRatio(card.ratio)" :class="ratioClass(card.ratio)">{{ ratioText(card.ratio) }}</span>
        <span v-else class="is-flat">-</span>
      </div>
    </div>

    <!-- 一张卡都没有时不需要那个占满一行高度的虚线框，缩成一个小按钮 -->
    <div v-if="canAdd" class="amc-add" :class="{ 'is-compact': !cards.length }">
      <div class="amc-add__box" :class="{ 'is-active': visible }" @click="togglePanel">
        + {{ t('addMetric') }}
      </div>
      <!-- 列出全部指标，已经加进卡片的那几个置灰 -->
      <ul v-show="visible" class="amc-menu">
        <li
          v-for="opt in metricOptions"
          :key="opt.value"
          :class="{ 'is-disabled': opt.disabled }"
          @click="pick(opt)"
        >{{ opt.label }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { metricColor } from '../../utils/metric'

export default {
  name: 'AbroadMetricCards',
  props: {
    // [{ key, name, text, ratio }]，文案由页面格式化好，这里只管画
    cards: {
      type: Array,
      default: () => []
    },
    // [{ value, label, disabled }]，下拉里列出的全部指标
    metricOptions: {
      type: Array,
      default: () => []
    },
    canAdd: {
      type: Boolean,
      default: true
    },
    // 时间对比开着就画同比那一行
    showRatio: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      visible: false,
      // 正在拖的那张卡，和 dragover 时经过的那张
      dragging: '',
      overKey: ''
    }
  },
  computed: {
    // 只有一张卡时就不需要进行拖拽了
    canDrag() {
      return this.cards.length > 1
    }
  },
  watch: {
    canAdd(val) {
      // 卡满 4 张时整个添加框都不在了，菜单状态要跟着收掉
      if (!val) this.visible = false
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocMouseDown)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocMouseDown)
  },
  methods: {
    t(key) {
      return this.$t('abroadStat.' + key)
    },
    // 圆点颜色跟着指标本身走，不跟卡片位置走
    dotColor(card) {
      return metricColor(card.key)
    },
    ratioText(val) {
      const num = Number(val)
      const arrow = num >= 0 ? '▲ ' : '▼ '
      return arrow + Math.abs(num) + '%'
    },
    hasRatio(val) {
      return val !== null && val !== undefined && val !== '' && !isNaN(Number(val))
    },
    ratioClass(val) {
      return Number(val) >= 0 ? 'is-up' : 'is-down'
    },
    togglePanel() {
      this.visible = !this.visible
    },
    onDocMouseDown(e) {
      if (!this.visible) return
      if (this.$refs.root && this.$refs.root.contains(e.target)) return
      this.visible = false
    },
    pick(opt) {
      if (opt.disabled) return
      this.visible = false
      this.$emit('add', opt.value)
    },
    // 拖拽换卡片顺序 
    cardClass(card) {
      const side = this.dropSide(card)
      return {
        'is-draggable': this.canDrag,
        'is-dragging': this.dragging === card.key,
        'is-drop-before': side === 'before',
        'is-drop-after': side === 'after'
      }
    },
    canDrop(card) {
      return !!this.dragging && this.dragging !== card.key
    },
    // 插入线画在目标卡的哪一侧。从左往右拖是插到目标之后、从右往左是插到之前
    dropSide(card) {
      if (this.overKey !== card.key || !this.canDrop(card)) return ''
      const keys = this.cards.map(one => one.key)
      return keys.indexOf(this.dragging) < keys.indexOf(card.key) ? 'after' : 'before'
    },
    onDragStart(card, e) {
      if (!this.canDrag) return
      this.dragging = card.key
      // 不设 setData 的话 firefox 不认这次拖拽
      if (e.dataTransfer) {
        e.dataTransfer.effectAllowed = 'move'
        try {
          e.dataTransfer.setData('text/plain', card.key)
        } catch (err) {
          // IE 下 setData 只认 'text'，拖不了就算了，增删卡片还能用
        }
      }
    },
    onDragOver(card, e) {
      if (!this.canDrop(card)) return
      // 不 preventDefault 就不会触发 drop
      e.preventDefault()
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
      this.overKey = card.key
    },
    onDragLeave(card) {
      if (this.overKey === card.key) this.overKey = ''
    },
    onDrop(card, e) {
      if (!this.canDrop(card)) return
      e.preventDefault()
      this.$emit('reorder', this.dragging, card.key)
      this.onDragEnd()
    },
    onDragEnd() {
      this.dragging = ''
      this.overKey = ''
    }
  }
}
</script>

<style lang="scss" scoped>
$main: #3361ff;
$text: #1f2329;
$text-sub: #4e5969;
$line: #f0f1f3;

.amc {
  display: flex;
  align-items: stretch;
  margin: 16px 30px;
}

.amc-card {
  position: relative;
  flex: 1;
  min-width: 0;
  padding: 18px 20px;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid $line;
  border-radius: 8px;

  & + .amc-card,
  & + .amc-add {
    margin-left: 16px;
  }
  &:hover .amc-card__remove {
    visibility: visible;
  }
  /* 整张卡都是拖拽区，指针跟着变。顺带禁掉选中 —— 卡上的文字本来也选不了了
     （draggable 的元素上按住拖，浏览器判成拖元素而不是拖选文字），
     不禁的话只会留下一层半高亮，看着像坏了 */
  &.is-draggable {
    cursor: move;
    user-select: none;
  }
  /* 拖起来的那张卡留个淡影，指明它离开了原位 */
  &.is-dragging {
    opacity: 0.4;
  }
  /* 插入位置的竖线，画在两张卡之间那 16px 的空当正中 */
  &.is-drop-before::before,
  &.is-drop-after::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: $main;
  }
  &.is-drop-before::before {
    left: -9px;
  }
  &.is-drop-after::before {
    right: -9px;
  }
}
.amc-card__head {
  display: flex;
  align-items: center;
}
.amc-card__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.amc-card__name {
  flex: 1;
  min-width: 0;
  margin-left: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 14px;
  color: $text-sub;
}
.amc-card__remove {
  flex: 0 0 auto;
  visibility: hidden;
  margin-left: 8px;
  padding: 2px;
  font-size: 14px;
  color: #c0c4cc;
  cursor: pointer;

  &:hover {
    color: $main;
  }
}
.amc-card__value {
  margin-top: 14px;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.2;
  color: $text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.amc-card__ratio {
  margin-top: 10px;
  height: 18px;
  line-height: 18px;
  font-size: 13px;

  .is-up {
    color: #00b42a;
  }
  .is-down {
    color: #f53f3f;
  }
  .is-flat {
    color: $text-sub;
  }
}
.amc-card__ratio-label {
  margin-right: 4px;
  color: #8f959e;
}

.amc-add {
  position: relative;
  flex: 0 0 150px;
  margin-left: 16px;

  /* 一张卡都没有：不撑高度，也不留左边距，和上面的筛选区对齐 */
  &.is-compact {
    flex: 0 0 auto;
    margin-left: 0;

    .amc-add__box {
      height: 32px;
      min-height: 0;
      padding: 0 16px;
    }
    /* 左边没有卡片挡着，菜单跟着按钮左对齐更自然 */
    .amc-menu {
      right: auto;
      left: 0;
    }
  }
}
.amc-add__box {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  /* 只在一张卡都没有时兜个高度，别的时候不能比卡片高，否则会把整行撑起来 */
  min-height: 88px;
  box-sizing: border-box;
  border: 1px dashed #b8c4ff;
  border-radius: 8px;
  font-size: 14px;
  color: $main;
  cursor: pointer;

  &:hover,
  &.is-active {
    border-color: $main;
    background: #f5f7ff;
  }
}
/* 菜单挂在添加框正下方、右对齐 */
.amc-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 2100;
  min-width: 150px;
  margin: 0;
  padding: 6px 0;
  list-style: none;
  background: #fff;
  border: 1px solid $line;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(31, 35, 41, 0.1), 0 2px 6px rgba(31, 35, 41, 0.04);

  li {
    height: 34px;
    line-height: 34px;
    padding: 0 20px;
    white-space: nowrap;
    font-size: 14px;
    color: $text;
    cursor: pointer;

    &:hover {
      background: #f5f6f7;
    }
    &.is-disabled {
      color: #c0c4cc;
      cursor: not-allowed;
      background: transparent;
    }
  }
}
</style>
